import React, { useEffect, useRef } from 'react';

import './src/@husky-x/gatsby-theme-husky/custom-local/gameScene.scss';

type SagoOptions = {
  deliveryHost?: string;
  injectCss?: boolean;
};

type SagoInstance = {
  destroy?: () => void;
  stop?: () => void;
};

declare global {
  interface Window {
    SagoSuperclean?: new (container: HTMLElement, options?: SagoOptions) => SagoInstance;
  }
}

const GAME_JS = 'https://game.sagosuperclean.de/sago_superclean.main.js';

/**
 * FIX #6 — Validate external script source (anti injection)
 */
const isValidScriptSource = (src: string): boolean => {
  try {
    const url = new URL(src);

    return url.origin === 'https://game.sagosuperclean.de';
  } catch {
    return false;
  }
};

/**
 * FIX #1, #6, #7 — Safe script loader
 */
const loadScriptOnce = (id: string, src: string): Promise<void> =>
  new Promise((resolve, reject) => {
    // FIX #6 — block invalid sources
    if (!isValidScriptSource(src)) {
      reject(new Error('Invalid script source'));

      return;
    }

    const existing = document.getElementById(id) as HTMLScriptElement | null;

    const onError = () => reject(new Error(`Failed to load ${src}`));

    if (existing) {
      if (existing.dataset.loaded === 'true') {
        resolve();

        return;
      }

      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', onError, { once: true });

      return;
    }

    const script = document.createElement('script');
    script.id = id;

    /**
     * FIX #7 — cache busting (avoid stale assets)
     */
    script.src = `${src}?v=1.0.0`;

    script.async = true;
    script.defer = true;

    script.addEventListener(
      'load',
      () => {
        script.dataset.loaded = 'true';
        resolve();
      },
      { once: true }
    );

    script.addEventListener('error', onError, { once: true });

    document.body.appendChild(script);
  });

const GameScene: React.FC = () => {
  const gameRef = useRef<HTMLDivElement | null>(null);
  const instanceRef = useRef<SagoInstance | null>(null);

  useEffect(() => {
    let cancelled = false;
    const container = gameRef.current;

    /**
     * FIX #4 — Block rendering inside iframe (anti-clickjacking)
     */
    const isInIframe = window.top !== window.self;

    if (isInIframe) {
      document.body.innerHTML = '';
    }

    (async () => {
      if (isInIframe) return;

      await loadScriptOnce('sago-superclean-js', GAME_JS);

      if (cancelled || !container) return;

      /**
       * FIX #1 — Prevent DOM XSS
       */
      container.replaceChildren();

      /**
       * FIX #1 — mark safe container
       */
      container.setAttribute('data-safe-container', 'true');

      const Ctor = window.SagoSuperclean;
      if (!Ctor) return;

      instanceRef.current = new Ctor(container, {
        deliveryHost: 'https://game.sagosuperclean.de',
        injectCss: true,
      });
    })();

    /**
     * ALWAYS return cleanup (FIX ESLint consistent-return)
     */
    return () => {
      cancelled = true;

      const inst = instanceRef.current;

      /**
       * 🧹 cleanup instance
       */
      if (inst?.destroy) inst.destroy();
      if (inst?.stop) inst.stop();

      instanceRef.current = null;

      /**
       * FIX #1 — safe DOM cleanup
       */
      if (container) {
        container.replaceChildren();
      }
    };
  }, []);

  return (
    <section id="game-scene">
      <h2 className="cds-heading">Jetzt spielen und teilnehmen!</h2>

      <div id="game" ref={gameRef} />
    </section>
  );
};

export default GameScene;
